import { useMemo, useState } from "react";
import { AlertTriangle, ChevronDown } from "lucide-react";
import {
  describeWaterEventReason,
  describeWaterEventWarning,
  operationReviewLevel,
  pumpOperationsNeedingReview,
  waterEventStateInfo,
} from "utils/waterEvents";

const TONE_CLASSES = {
  good: "bg-emerald-100 text-emerald-800",
  warn: "bg-amber-100 text-amber-800",
  bad: "bg-red-100 text-red-800",
  active: "bg-blue-100 text-blue-800",
  neutral: "bg-slate-100 text-slate-700",
};

// The toggle and each row take the colour of what needs review: red for a failure, amber for a skip or
// a warning.
const TOGGLE_CLASSES = {
  bad: "border-red-300 bg-red-50 text-red-800",
  warn: "border-amber-300 bg-amber-50 text-amber-800",
  none: "border-slate-200 bg-white text-slate-700",
};
const FLAG_CLASSES = { bad: "text-red-600", warn: "text-amber-500" };
const ROW_CLASSES = {
  bad: "border-red-200 bg-red-50",
  warn: "border-amber-200 bg-amber-50",
  none: "border-slate-200 bg-slate-50",
};

/**
 * The pump's operations (scheduled by the experiment, or started by hand), folded behind a button at the
 * bottom of the Pump card. Photos are opened and labelled from the water-level chart's camera markers, so
 * this is only the log: what ran, how long the pump ran, and why one was skipped or failed. The button is
 * flagged (red / amber, with a count) when an operation of the run on the chart needs review.
 *
 * Props: events (newest first), error, runId / start / end (the run on the chart: ALL of its operations
 * are listed, and only they can flag), limit (how many rows in total before the older ones fold behind
 * "Show N earlier", default 10).
 */
export default function PumpOperationsPanel({ events = [], error, limit = 10, runId = null, start = null, end = null }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const review = useMemo(
    () => pumpOperationsNeedingReview(events, { runId, start, end, fallbackCount: limit }),
    [events, runId, start, end, limit]
  );

  // Real bug (2026-10-07): a plan with 11 events showed only the newest 10, so the first one ran but
  // had no row. The run being watched is never cut; only other runs' / manual operations are.
  let spare = Math.max(0, limit - (runId ? events.filter((event) => event.run_id === runId).length : 0));
  const shown = expanded
    ? events
    : events.filter((event) => {
        if (runId && event.run_id === runId) return true;
        if (spare > 0) {
          spare -= 1;
          return true;
        }
        return false;
      });
  const hidden = events.length - shown.length;
  const toReview = review.items.length;

  return (
    <div className="mt-3 border-t border-slate-100 pt-3" data-testid="pump-operations-panel">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        data-testid="pump-operations-toggle"
        className={`flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2 text-xs font-bold ${
          TOGGLE_CLASSES[review.level || "none"]
        }`}
      >
        <span className="flex items-center gap-2">
          {review.level && (
            <AlertTriangle
              data-testid="pump-operations-flag"
              data-level={review.level}
              aria-hidden="true"
              className={`h-4 w-4 ${FLAG_CLASSES[review.level]}`}
            />
          )}
          <span>
            Pump operations{" "}
            {toReview > 0 ? (
              <span className="font-semibold">· {toReview} to review</span>
            ) : (
              <span className="font-medium text-slate-400">({events.length})</span>
            )}
          </span>
        </span>
        <ChevronDown aria-hidden="true" className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {error && <p role="alert" className="mt-2 text-xs text-red-700">Could not load the pump operations: {error}</p>}

      {open && (
        <div data-testid="pump-operations-list">
          {shown.length === 0 && !error && (
            <p className="mt-2 text-xs text-slate-500">
              No pump operations yet. Scheduled water events and manual Fill/Drain show up here.
            </p>
          )}
          <ul className="mt-2 flex flex-col gap-2">
            {shown.map((event) => {
              const info = waterEventStateInfo(event.state);
              const reason = describeWaterEventReason(event.skip_reason);
              const warnings = describeWaterEventWarning(event.warning);
              const level = operationReviewLevel(event);
              return (
                <li
                  key={event.id}
                  data-testid={`pump-operation-${event.id}`}
                  data-review={level || ""}
                  className={`rounded-lg border px-3 py-2 text-xs ${ROW_CLASSES[level || "none"]}`}
                >
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-semibold text-slate-900">
                      {event.direction === "drain" ? "Drain" : "Fill"} {event.cm} cm
                    </span>
                    <span className="text-slate-500">
                      {event.source === "manual" ? "by hand" : `scheduled${event.run_id ? ` (${event.run_id})` : ""}`}
                    </span>
                    <span className="text-slate-500">{new Date(event.due_at).toLocaleString()}</span>
                    <span
                      data-testid={`pump-operation-${event.id}-state`}
                      className={`rounded-full px-2 py-0.5 font-medium ${TONE_CLASSES[info.tone] || TONE_CLASSES.neutral}`}
                    >
                      {info.label}
                    </span>
                  </div>
                  {event.pump_elapsed_ms != null && (
                    <p className="mt-1 text-slate-600" data-testid={`pump-operation-${event.id}-timing`}>
                      Pumped {(event.pump_elapsed_ms / 1000).toFixed(1)} s
                      {event.expected_seconds != null ? ` (commanded ${event.expected_seconds} s)` : ""}
                    </p>
                  )}
                  {reason && <p className="mt-1 text-amber-800">{reason}</p>}
                  {warnings.map((warning) => (
                    <p key={warning} className="mt-1 text-amber-800">{warning}</p>
                  ))}
                </li>
              );
            })}
          </ul>
          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700"
            >
              Show {hidden} earlier operation{hidden === 1 ? "" : "s"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
